import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type {
	BulkNotificationShipment,
	BulkNotificationTarget,
	CreateRecipientBody,
	ExternalCreateRecipientBody,
	ExternalSaveIndividualRecipientsBody,
	SaveIndividualRecipientsBody,
	ScopedBulkNotificationDelta,
	UnifiedIndividualNotificationRecipient,
	UnifiedNotificationRecipient,
	UpdateRecipientBody,
} from "~/shared/schemas/shipment-notification-schema.ts";
import {
	bulkNotificationRecipientInputSchema,
	bulkNotificationTargetSchema,
	bulkNotificationUnsubscribeInputSchema,
	externalBulkNotificationShipmentSchema,
	externalIndividualNotificationRecipientSchema,
	externalNotificationRecipientSchema,
	externalToUnifiedIndividualRecipient,
	externalToUnifiedRecipient,
	individualNotificationRecipientSchema,
	internalBulkNotificationShipmentSchema,
	notificationRecipientSchema,
	toUnifiedIndividualRecipient,
	toUnifiedRecipient,
} from "~/shared/schemas/shipment-notification-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	deleteAndValidate,
	deleteAndValidateTraxAr,
	getAndValidate,
	getAndValidateTraxAr,
	postAndValidate,
	postAndValidateTraxAr,
	putAndValidate,
	putAndValidateTraxAr,
} from "~/shared/services/api/fetch-client.ts";
import { getIsInternal } from "~/shared/utils/get-deployment-type.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

const deleteResponseSchema = z.union([
	z.void(),
	z.null(),
	z.string(),
	z.object({}).passthrough(),
]);

const recipientsQueryKey = (
	customerId: number,
	poNumber?: number,
): (number | string | undefined)[] => [
	"shipmentNotificationRecipients",
	customerId,
	...(poNumber === undefined ? [] : [poNumber]),
];

const bulkRecipientsQueryKey = (
	targets: readonly BulkNotificationTarget[],
): (number | string)[] => [
	"shipmentNotificationRecipientsBulk",
	...targets
		.map(
			(target) =>
				`${String(target.customerId ?? 0)}:${String(target.poNumber)}`,
		)
		.sort((left, right) => left.localeCompare(right)),
];

export function useNotificationRecipients(
	customerId: number,
	poNumber: number,
): ReturnType<typeof useQuery<UnifiedIndividualNotificationRecipient[]>>;
export function useNotificationRecipients(
	customerId: number,
): ReturnType<typeof useQuery<UnifiedNotificationRecipient[]>>;
export function useNotificationRecipients(
	customerId: number,
	poNumber?: number,
) {
	return useQuery({
		queryKey: recipientsQueryKey(customerId, poNumber),
		queryFn: async () => {
			const isInternal = getIsInternal();

			if (poNumber !== undefined) {
				return fetchIndividualRecipients(isInternal, customerId, poNumber);
			}

			return fetchRecipients(isInternal, customerId);
		},
		enabled:
			poNumber === undefined ? customerId > 0 : customerId > 0 && poNumber > 0,
	});
}

async function fetchRecipients(
	isInternal: boolean,
	customerId: number,
): Promise<UnifiedNotificationRecipient[]> {
	if (isInternal) {
		const result = await getAndValidateTraxAr(
			`${apiRoutes.SHIPMENTNOTIFICATIONRECIPIENTS}?customerId=${String(customerId)}`,
			notificationRecipientSchema.array(),
		);

		if (!result.success) {
			throw new Error(result.error);
		}

		return result.data.map((r) => toUnifiedRecipient(r));
	}

	const result = await getAndValidate(
		apiRoutes.EXTERNALSHIPMENTNOTIFICATIONRECIPIENTS,
		externalNotificationRecipientSchema.array(),
	);

	if (!result.success) {
		throw new Error(result.error);
	}

	return result.data.map((r) => externalToUnifiedRecipient(r));
}

async function fetchIndividualRecipients(
	isInternal: boolean,
	customerId: number,
	poNumber: number,
): Promise<UnifiedIndividualNotificationRecipient[]> {
	if (isInternal) {
		const result = await getAndValidateTraxAr(
			`${apiRoutes.SHIPMENTNOTIFICATIONRECIPIENTSINDIVIDUAL}?customerId=${String(customerId)}&poNumber=${String(poNumber)}`,
			individualNotificationRecipientSchema.array(),
		);

		if (!result.success) {
			throw new Error(result.error);
		}

		return result.data.map((r) => toUnifiedIndividualRecipient(r));
	}

	const result = await getAndValidate(
		`${apiRoutes.EXTERNALSHIPMENTNOTIFICATIONRECIPIENTSINDIVIDUAL}?poNumber=${String(poNumber)}`,
		externalIndividualNotificationRecipientSchema.array(),
	);

	if (!result.success) {
		throw new Error(result.error);
	}

	return result.data.map((r) => externalToUnifiedIndividualRecipient(r));
}

async function fetchBulkRecipients(
	targets: readonly BulkNotificationTarget[],
): Promise<BulkNotificationShipment[]> {
	const isInternal = getIsInternal();

	if (isInternal) {
		const shipments = targets.flatMap((target) =>
			target.customerId == null
				? []
				: [{ customerId: target.customerId, poNumber: target.poNumber }],
		);
		if (shipments.length !== targets.length) {
			throw new Error(
				"Customer information is required for internal shipments.",
			);
		}

		const result = await postAndValidateTraxAr(
			apiRoutes.SHIPMENTNOTIFICATIONRECIPIENTSINDIVIDUALBULKQUERY,
			{ shipments },
			internalBulkNotificationShipmentSchema.array(),
		);

		if (!result.success) {
			throw new Error(result.error);
		}

		return result.data.map((shipment) => ({
			customerId: shipment.customerId,
			poNumber: shipment.poNumber,
			recipients: shipment.recipients.map((recipient) =>
				toUnifiedIndividualRecipient(recipient),
			),
		}));
	}

	const result = await postAndValidate(
		apiRoutes.EXTERNALSHIPMENTNOTIFICATIONRECIPIENTSINDIVIDUALBULKQUERY,
		{ poNumbers: targets.map((target) => target.poNumber) },
		externalBulkNotificationShipmentSchema.array(),
	);

	if (!result.success) {
		throw new Error(result.error);
	}

	return result.data.map((shipment) => ({
		customerId: null,
		poNumber: shipment.poNumber,
		recipients: shipment.recipients.map((recipient) =>
			externalToUnifiedIndividualRecipient(recipient),
		),
	}));
}

export const useBulkNotificationRecipients = (
	targets: readonly BulkNotificationTarget[],
) =>
	useQuery({
		queryKey: bulkRecipientsQueryKey(targets),
		queryFn: () => fetchBulkRecipients(targets),
		// Subscriptions can be changed from other surfaces, so read through on every open
		// rather than trusting a snapshot cached behind the global 5 minute stale time.
		refetchOnMount: "always",
		enabled:
			targets.length > 0 &&
			targets.every(
				(target) =>
					target.poNumber > 0 &&
					(getIsInternal()
						? target.customerId != null && target.customerId > 0
						: true),
			),
	});

export const useCreateNotificationRecipient = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotificationActions();
	const { employeeId } = useIsAuthenticated();

	return useMutation({
		mutationFn: async (
			body: CreateRecipientBody | ExternalCreateRecipientBody,
		) => {
			const isInternal = getIsInternal();

			if (isInternal) {
				const result = await postAndValidateTraxAr(
					`${apiRoutes.SHIPMENTNOTIFICATIONRECIPIENTS}?employeeid=${employeeId}`,
					body,
					notificationRecipientSchema,
				);

				if (!result.success) {
					throw new Error(result.error);
				}

				return toUnifiedRecipient(result.data);
			}

			const result = await postAndValidate(
				apiRoutes.EXTERNALSHIPMENTNOTIFICATIONRECIPIENTS,
				body,
				externalNotificationRecipientSchema,
			);

			if (!result.success) {
				throw new Error(result.error);
			}

			return externalToUnifiedRecipient(result.data);
		},
		onSuccess: () => {
			showSuccess("Notification recipient created successfully.");
			queryClient.removeQueries({
				queryKey: ["shipmentNotificationRecipients"],
			});
			void queryClient.invalidateQueries({
				queryKey: ["shipmentNotificationTypes"],
			});
		},
		onError: (error: Error) => {
			console.error(error);
			showError(
				"Failed to create notification recipient. Please try again later.",
			);
		},
	});
};

export const useUpdateNotificationRecipient = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotificationActions();
	const { employeeId } = useIsAuthenticated();

	return useMutation({
		mutationFn: async ({
			id,
			body,
		}: {
			id: string;
			body: UpdateRecipientBody;
		}) => {
			const isInternal = getIsInternal();

			if (isInternal) {
				const route = apiRoutes.SHIPMENTNOTIFICATIONRECIPIENT.replace(
					":id",
					String(id),
				);

				const result = await putAndValidateTraxAr(
					`${route}?employeeid=${employeeId}`,
					body,
					notificationRecipientSchema,
				);

				if (!result.success) {
					throw new Error(result.error);
				}

				return toUnifiedRecipient(result.data);
			}

			const route = apiRoutes.EXTERNALSHIPMENTNOTIFICATIONRECIPIENT.replace(
				":id",
				String(id),
			);

			const result = await putAndValidate(
				route,
				body,
				externalNotificationRecipientSchema,
			);

			if (!result.success) {
				throw new Error(result.error);
			}

			return externalToUnifiedRecipient(result.data);
		},
		onSuccess: () => {
			showSuccess("Notification recipient updated successfully.");
			queryClient.removeQueries({
				queryKey: ["shipmentNotificationRecipients"],
			});
			void queryClient.invalidateQueries({
				queryKey: ["shipmentNotificationTypes"],
			});
		},
		onError: (error: Error) => {
			console.error(error);
			showError(
				"Failed to update notification recipient. Please try again later.",
			);
		},
	});
};

export const useDeleteNotificationRecipient = (customerId: number) => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotificationActions();

	return useMutation({
		mutationFn: async (id: string) => {
			const isInternal = getIsInternal();

			if (isInternal) {
				const route = apiRoutes.SHIPMENTNOTIFICATIONRECIPIENT.replace(
					":id",
					String(id),
				);

				const result = await deleteAndValidateTraxAr(
					route,
					deleteResponseSchema,
				);

				if (!result.success) {
					throw new Error(result.error);
				}

				return result;
			}

			const route = apiRoutes.EXTERNALSHIPMENTNOTIFICATIONRECIPIENT.replace(
				":id",
				String(id),
			);

			const result = await deleteAndValidate(route, deleteResponseSchema);

			if (!result.success) {
				throw new Error(result.error);
			}

			return result;
		},
		onSuccess: () => {
			showSuccess("Notification recipient deleted successfully.");
			void queryClient.invalidateQueries({
				queryKey: recipientsQueryKey(customerId),
			});
		},
		onError: (error: Error) => {
			console.error(error);
			showError(
				"Failed to delete notification recipient. Please try again later.",
			);
		},
	});
};

export const useSaveIndividualRecipients = (
	customerId: number,
	poNumber: number,
) => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotificationActions();
	const { employeeId } = useIsAuthenticated();

	return useMutation({
		mutationFn: async (
			body: SaveIndividualRecipientsBody | ExternalSaveIndividualRecipientsBody,
		) => {
			const isInternal = getIsInternal();

			if (isInternal) {
				const result = await putAndValidateTraxAr(
					`${apiRoutes.SHIPMENTNOTIFICATIONRECIPIENTSINDIVIDUAL}?employeeid=${employeeId}`,
					body,
					individualNotificationRecipientSchema.array(),
				);

				if (!result.success) {
					throw new Error(result.error);
				}

				return result.data.map((r) => toUnifiedIndividualRecipient(r));
			}

			const result = await putAndValidate(
				apiRoutes.EXTERNALSHIPMENTNOTIFICATIONRECIPIENTSINDIVIDUAL,
				body,
				externalIndividualNotificationRecipientSchema.array(),
			);

			if (!result.success) {
				throw new Error(result.error);
			}

			return result.data.map((r) => externalToUnifiedIndividualRecipient(r));
		},
		onSuccess: () => {
			showSuccess("Shipment notification recipients saved successfully.");
			void queryClient.invalidateQueries({
				queryKey: recipientsQueryKey(customerId, poNumber),
			});
		},
		onError: (error: Error) => {
			console.error(error);
			showError(
				"Failed to save notification recipients. Please try again later.",
			);
		},
	});
};

export const useSaveBulkNotificationRecipients = (
	targets: readonly BulkNotificationTarget[],
) => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotificationActions();
	const { employeeId } = useIsAuthenticated();

	return useMutation({
		mutationFn: async (
			operations: ScopedBulkNotificationDelta[],
		): Promise<BulkNotificationShipment[]> => {
			const isInternal = getIsInternal();
			const responses = await Promise.all(
				operations.map(async (operation) => {
					const operationTargets = bulkNotificationTargetSchema
						.array()
						.parse(operation.targets);
					const subscribe = bulkNotificationRecipientInputSchema
						.array()
						.parse(operation.subscribe);
					const unsubscribe = bulkNotificationUnsubscribeInputSchema
						.array()
						.parse(operation.unsubscribe);

					if (isInternal) {
						const shipments = operationTargets.flatMap((target) =>
							target.customerId == null
								? []
								: [
										{
											customerId: target.customerId,
											poNumber: target.poNumber,
										},
									],
						);
						if (shipments.length !== operationTargets.length) {
							throw new Error(
								"Customer information is required for internal shipments.",
							);
						}

						const result = await putAndValidateTraxAr(
							`${apiRoutes.SHIPMENTNOTIFICATIONRECIPIENTSINDIVIDUALBULK}?employeeid=${employeeId}`,
							{ shipments, recipients: subscribe, unsubscribe },
							internalBulkNotificationShipmentSchema.array(),
						);

						if (!result.success) throw new Error(result.error);

						return result.data.map((shipment) => ({
							customerId: shipment.customerId,
							poNumber: shipment.poNumber,
							recipients: shipment.recipients.map((recipient) =>
								toUnifiedIndividualRecipient(recipient),
							),
						}));
					}

					const result = await putAndValidate(
						apiRoutes.EXTERNALSHIPMENTNOTIFICATIONRECIPIENTSINDIVIDUALBULK,
						{
							poNumbers: operationTargets.map((target) => target.poNumber),
							recipients: subscribe,
							unsubscribe,
						},
						externalBulkNotificationShipmentSchema.array(),
					);

					if (!result.success) throw new Error(result.error);

					return result.data.map((shipment) => ({
						customerId: null,
						poNumber: shipment.poNumber,
						recipients: shipment.recipients.map((recipient) =>
							externalToUnifiedIndividualRecipient(recipient),
						),
					}));
				}),
			);

			return responses.flat();
		},
		onSuccess: () => {
			showSuccess("Shipment notification recipients saved successfully.");
		},
		onSettled: () => {
			void queryClient.invalidateQueries({
				queryKey: bulkRecipientsQueryKey(targets),
			});
		},
		onError: (error: Error) => {
			console.error(error);
			showError(
				"Failed to save notification recipients. Please try again later.",
			);
		},
	});
};
